import classNames from "classnames";
import { FormikProps } from "formik";
import { ReactNode, useCallback, useContext, useMemo } from "react";
import {
  CreationClassExtended,
  ItemViewModel,
  SubmitCreation,
} from "../../../../generated/data-contracts";
import { CreationType } from "../../../../models";
import {
  inputValidateAlphanumeric,
  inputValidateAlphanumericPaste,
} from "../../../../utils";
import { isMobileView } from "../../../shared/appStates/appStates";
import { AvisConfirmButton } from "../../../shared/components/buttons";
import { AvisSection } from "../../../shared/components/form";
import {
  AvisDropdown,
  AvisInputMaskTiming,
  AvisInputText,
  AvisMultiline,
} from "../../../shared/components/inputs";
import { AvisContext } from "../../../shared/context/AvisContext";
import { CreationNewBands } from "./sections/CreationNewBands";
import { CreationNewFiles } from "./sections/CreationNewFiles";
import { CreationNewRightHolders } from "./sections/CreationNewRightsHolders";

interface IAvisInputField {
  creationClassCodes: CreationType[];
  name: string;
  component: ReactNode;
}
interface ICreationsNewFormProps {
  creationType: CreationType;
  creationTypeReadOnly?: boolean;
  bag: FormikProps<SubmitCreation>;
  isLoading: boolean;
  isSubmitting: boolean;
  isSubmitted: boolean;
  creationClasses: CreationClassExtended[] | undefined;
  genres: ItemViewModel[] | undefined;
  roles: ItemViewModel[] | undefined;
  allowedFileFormats: string[] | undefined;
  hideButtons?: boolean;
  showModal: () => void;
  resetForm: () => void;
  onSelectedCreationClassItemChange: (val: number) => void;
}

export function CreationsNewForm(props: Readonly<ICreationsNewFormProps>) {
  const {
    creationType,
    bag,
    isLoading,
    isSubmitting,
    isSubmitted,
    creationClasses,
    genres,
    roles,
    allowedFileFormats,
    hideButtons,
    creationTypeReadOnly,
    showModal,
    resetForm,
    onSelectedCreationClassItemChange,
  } = props;
  const { res } = useContext(AvisContext);
  const fieldColClass = isMobileView.value ? "col-12" : "col-6";
  const fieldGridClass = classNames("form-field", "pr-2", fieldColClass);

  const arrayOfConfigs: IAvisInputField[] = useMemo(
    () => [
      {
        creationClassCodes: ["M", "H", "PPI"],
        name: "creationClassId",
        component: (
          <AvisDropdown<ItemViewModel, SubmitCreation>
            bag={bag}
            bagField="creationClassId"
            className="creation-class-dropdown"
            label={res("label_CreationClassName")}
            options={creationClasses}
            optionValue="id"
            optionLabel="name"
            hideClear
            required
            disabled={isSubmitted || isSubmitting || creationTypeReadOnly}
            onChange={(e) => {
              onSelectedCreationClassItemChange(e.value);
            }}
          />
        ),
      },
      {
        creationClassCodes: ["M", "H", "PPI"],
        name: "creationName",
        component: (
          <AvisInputText<SubmitCreation>
            bag={bag}
            bagField="creationName"
            label={res("authors_Creation_label_CreationName")}
            disabled={isSubmitted || isSubmitting}
            maxLength={200}
            required
          />
        ),
      },
      {
        creationClassCodes: ["M", "PPI"],
        name: "creationGenreId",
        component: (
          <AvisDropdown<ItemViewModel, SubmitCreation>
            bag={bag}
            bagField="creationGenreId"
            label={res("authors_Creation_label_Genre")}
            options={genres}
            optionValue="id"
            optionLabel="name"
            required
            disabled={isSubmitted || isSubmitting}
          />
        ),
      },
      {
        creationClassCodes: ["M", "H"],
        name: "timings",
        component: (
          <AvisInputMaskTiming<SubmitCreation>
            bag={bag}
            bagField="timings"
            label={res("authors_Creation_label_Timing")}
            disabled={isSubmitted || isSubmitting}
            required
          />
        ),
      },
      {
        creationClassCodes: ["PPI"],
        name: "manufactureYear",
        component: (
          <AvisInputText<SubmitCreation>
            bag={bag}
            bagField="manufactureYear"
            label={res("authors_Creation_label_ManufactureYear")}
            disabled={isSubmitted || isSubmitting}
            maxLength={100}
          />
        ),
      },
      {
        creationClassCodes: ["PPI"],
        name: "publisher",
        component: (
          <AvisInputText<SubmitCreation>
            bag={bag}
            bagField="publisher"
            label={res("authors_Creation_label_Publisher")}
            maxLength={100}
            disabled={isSubmitted || isSubmitting}
          />
        ),
      },
      {
        creationClassCodes: ["PPI"],
        name: "isbnNumber",
        component: (
          <AvisInputText<SubmitCreation>
            bag={bag}
            bagField="isbnNumber"
            label={res("authors_Creation_label_ISBN")}
            maxLength={50}
            disabled={isSubmitted || isSubmitting}
            onKeyDown={inputValidateAlphanumeric}
            onPaste={inputValidateAlphanumericPaste}
          />
        ),
      },
      {
        creationClassCodes: ["M", "H"],
        name: "creationAlternativeName",
        component: (
          <AvisInputText<SubmitCreation>
            bag={bag}
            bagField="creationAlternativeName"
            label={res("authors_Creation_label_AlternativeName")}
            maxLength={200}
            disabled={isSubmitted || isSubmitting}
          />
        ),
      },
    ],
    [
      bag,
      creationClasses,
      creationTypeReadOnly,
      genres,
      isSubmitted,
      isSubmitting,
      onSelectedCreationClassItemChange,
      res,
    ],
  );

  const onSubmitPrompt = useCallback(async () => {
    const errors = await bag.validateForm();
    if (Object.keys(errors).length > 0) return;

    showModal();
  }, [bag, showModal]);

  const registerAnother = useCallback(() => {
    resetForm();
    bag.resetForm();
  }, [bag, resetForm]);
  const classNameCreationNewForm = classNames("creation-new-form", "grid");

  const creationTextKeyPrefix = useMemo(() => {
    return `portal_RH_NewCreation_${creationType}`;
  }, [creationType]);
  return (
    <>
      <div className={classNameCreationNewForm}>
        {arrayOfConfigs
          .filter((el) => el.creationClassCodes.indexOf(creationType) > -1)
          .map((el) => {
            return (
              <div key={el.name} className={fieldGridClass}>
                {el.component}
              </div>
            );
          })}
      </div>
      <CreationNewRightHolders
        bag={bag}
        bagField="rightsHolders"
        roles={roles}
        disabled={isSubmitted || isSubmitting}
        textKey={`${creationTextKeyPrefix}_1`}
      />
      {["M", "H"].indexOf(creationType) > -1 && (
        <CreationNewBands
          bag={bag}
          bagField="creationBands"
          disabled={isSubmitted || isSubmitting}
          textKey={`${creationTextKeyPrefix}_2`}
        />
      )}
      {["M", "H", "PPI"].indexOf(creationType) > -1 && (
        <CreationNewFiles
          bag={bag}
          bagField="creationFiles"
          disabled={isSubmitted || isSubmitting}
          allowedFileFormats={allowedFileFormats}
          textKey={`${creationTextKeyPrefix}_3`}
        />
      )}
      <AvisSection title={res("label_Notes")} classNameIn="col-12">
        <AvisMultiline<SubmitCreation>
          bag={bag}
          rows={4}
          bagField="notes"
          maxLength={500}
          disabled={isSubmitted || isSubmitting}
        />
      </AvisSection>
      {!hideButtons && (
        <div className="col-12 pt-3">
          <AvisConfirmButton
            className="mb-3 mr-3 col-3"
            label={res("authors_CreationNew_button_AddCreation")}
            onClick={onSubmitPrompt}
            disabled={isLoading || isSubmitting || isSubmitted}
          />

          {isSubmitted && (
            <AvisConfirmButton
              label={res("authors_CreationNew_button_FillAnotherCreation")}
              onClick={registerAnother}
            />
          )}
        </div>
      )}
    </>
  );
}
